import {ElementRef, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import { BaseComponent } from '../../views/theme/base/base.component';
import { FileModel } from '../models/file.model';

/**
 * order-go-3d-viewer/Assets/Scripts/ViewerController.csのCameraModelクラスを参照
 * 
 * C#の命名規則に従ってパスカルケース
 * 
 * @see https://github.com/tannakaken/order-go-3d-viewer/blob/master/Assets/Scripts/ViewerController.cs
 */
export type CameraModel = {
    /**
     * 整数
     */
    Id: number;
    /**
     * 実数
     */
    CameraX: number;
    /**
     * 実数
     */
    CameraY: number;
    /**
     * 実数
     */
    CameraZ: number;
    /**
     * 実数
     */
    AimX: number;
    /**
     * 実数
     */
    AimY: number;
    /**
     * 実数
     */
    AimZ: number;
    /**
     * CAMERA_TYPE.HUMANとCAMERA_TYPE.BIRDの二種類があるようだ。
     * three.jsでは使わない
     */
    Type: "CAMERA_TYPE.HUMAN" | "CAMERA_TYPE.BIRD";
    /**
     * 実数
     * 
     * なぜ、ViewAngleではなくViewAngleIdなのか、全く意味不明！？？？？？？
     * Unityの実装を見るにただの実数値のようだ
     * 
     * three.jsでは使わない
     */
    ViewAngleId: number;
    /**
     * 歪み補正
     * 
     * three.jsでは使わない
     */
    PerspectiveCorrection: boolean;
}

export type CameraModelExtraForThree = {
    /**
     * オブジェクトのオイラー角
     */
    objectRotateX: number;
    /**
     * オブジェクトのオイラー角
     */
    objectRotateY: number;
    /**
     * オブジェクトのオイラー角
     */
    objectRotateZ: number;
}

/**
 * order-go-3d-viewer/Assets/Scripts/ViewerController.csのCameraSettingsクラスを参照
 * 
 * @see https://github.com/tannakaken/order-go-3d-viewer/blob/master/Assets/Scripts/ViewerController.cs
 */
export type CameraSettings = {
    /**
     * 整数
     */
    FileId: number;
    /**
     * カメラの配列だが、通常1個だけ入っている。
     */
    Data: [CameraModel];
    /**
     * three.jsのためのデータをより分ける
     */
    isThree?: undefined;
} | {
    /**
     * 整数
     */
    FileId: number;
    /**
     * カメラの配列だが、通常1個だけ入っている。
     */
    Data: [CameraModel & CameraModelExtraForThree];
    /**
     * three.jsのためのデータをより分ける
     */
    isThree: true;
}

/**
 * 事前にUnityLoader.jsで読み込まれるグローバルオブジェクト。
 */
declare var UnityLoader: any;
/**
 * 事前にUnityProgress.jsで読み込まれるグローバルオブジェクト。
 * 
 * 多分、Unity読み込みのプログレスバーを出してくれるんだと思う。
 */
declare var UnityProgress: any;
declare global {
    interface Window {
        /**
         * Unityのゲームオブジェクトのメソッドを呼び出すためのオブジェクト
         * 
         * この場合は全てorder-go-3d-viewer/Assets/Scenes/Viewerシーンの
         * ゲームオブジェクトSceneControllerにアタッチされたScriptオブジェクトorder-go-3d-viewer/Assets/Scripts/ViewerControllerのメソッドを呼び出している。
         * 
         * @see https://docs.unity3d.com/ja/2018.4/Manual/webgl-interactingwithbrowserscripting.html
         */
        unityInstance: {
            /**
             * Unityのシーン中のゲームオブジェクトのメソッドを呼び出すための関数
             */
            SendMessage: (gameObjectName: string, methodName: string, argument?: string | number | boolean) => void
        } | undefined;
        /**
         * order-go-3d-viewer/Assets/Plugins/JavaScript/Loader.jslibのisReady関数で追加されているグローバル値
         * 
         * @see https://docs.unity3d.com/ja/2018.4/Manual/webgl-interactingwithbrowserscripting.html
         */
        unityInstanceIsReady: boolean | undefined;
    }
}

@Injectable()
export class Unity3DViewerService {
    /**
     * 3Dモデルのキャッシュ。使う際は必ずcloneして使うこと。
     * webglコンテクストはキャンバス同士で共有ができないので、一つのcanvasにしか紐づけられないので、あるcanvasに紐づけられたオブジェクトを
     * 他のcanvasに紐づけると、前のcanvasが白飛びする（webgpuだとどうなるかは調査していない）。
     */
    private objCache: WeakMap<FileModel, THREE.Group<THREE.Object3DEventMap>> = new WeakMap();
    constructor() {
    }

    init(canvasWidth: number, canvasHeight: number, registerCallback: (unityContainer: HTMLElement) => void) {
        const unityContainer = document.getElementById('unityContainer');
        if (!unityContainer) {
            return;
        }
        registerCallback(unityContainer);
        unityContainer.style.width = `${canvasWidth}px`;
        unityContainer.style.height = `${canvasHeight}px`;
        window.unityInstance = UnityLoader.instantiate(
            'unityContainer',
            environment.unity3DViewerUrl,
            {onProgress: UnityProgress});
        
    }

    loadFile(fileId: number, fileUrl: string) {
        window.unityInstance?.SendMessage('SceneController', 'LoadFile', fileId + '|' + fileUrl);
    }

    /**
     * order-go-3d-viewer/Assets/Scripts/ViewerControllerのSaveCameraSettings内で、
     * Unity内のCameraSettingsをシリアライズしたjsonを引数に
     * order-go-3d-viewer/Assets/Plugins/javascript/Loader.jslib内のSaveCameraSettings関数を呼び出し、
     * それがwindow.baseComponentRef.saveCameraSettingsfnを呼び出す。
     * 
     * その正体は/client/web/src/app/views/theme/base/base.component.tsの{@link BaseComponent#saveCameraSettings}関数である。
     */
    saveCameraSettings() {
        window.unityInstance?.SendMessage('SceneController', 'SaveCameraSettings');
    }

    loadCameraSettings(cameraSettings: string): CameraSettings | undefined {
        if (window.unityInstance) {
            window.unityInstance.SendMessage('SceneController', 'LoadCameraSettings', cameraSettings);
            try {
                return JSON.parse(cameraSettings);
            } catch (error) {
                console.error(error);
            }
        }
    }

    focusCanvas(focus = true) {
        window.unityInstance?.SendMessage('SceneController', 'FocusCanvas', focus ? "1": "0");
    }

    /**
     * 初期化済みのunityContainerが消えないように、一時避難させる。
     */
    evacuateContainer() {
        const unityContainer = document.getElementById('unityContainer');
        if (!unityContainer) {
            return;
        }
        const mainUnityContainerWrapper = document.getElementById('mainUnityContainerWrapper');
        if (!mainUnityContainerWrapper) {
            return;
        }
        mainUnityContainerWrapper.appendChild(unityContainer);
    }

    /**
     * 画面遷移後やリサイズ後にunityContainerを復帰させるのに使う。
     */
    resetUnityContainer(unityContainerWrapper: ElementRef<HTMLElement>, canvasWidth: number, canvasHeight: number) {
        const unityContainer = document.getElementById('unityContainer');
        if (!unityContainer) {
            return;
        }
        const unityContainerNativeElement = unityContainerWrapper.nativeElement;
        unityContainerNativeElement.style.width = `${canvasWidth}px`;
        unityContainerNativeElement.style.height = `${canvasHeight}px`;
        unityContainerWrapper.nativeElement.appendChild(unityContainer);
        unityContainer.style.width = `${canvasWidth}px`;
        unityContainer.style.height = `${canvasHeight}px`;
    }

    setObjCache(file: FileModel, object: THREE.Group<THREE.Object3DEventMap>) {
        this.objCache.set(file, object);
    }

    getObjCache(file: FileModel): THREE.Group<THREE.Object3DEventMap> | undefined {
        return this.objCache.get(file)?.clone();
    }
}